import { Polygon } from "@react-google-maps/api";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { EditableFence } from "./types";
import { isSameShape, readPolygonPath } from "./mapUtils";

type PolygonContainerProps = {
  fence: EditableFence;
  isSelected: boolean;
  onSelect: (locationId: string) => void;
  onHover: (locationId: string | null) => void;
  onShapeChange: (fence: EditableFence) => void;
};

export const FENCE_STYLES: Record<
  "selected" | EditableFence["status"],
  google.maps.PolygonOptions
> = {
  selected: { fillColor: "#6610f2", strokeColor: "#6610f2" }, // violet
  NEW: { fillColor: "#007bff", strokeColor: "#0056b3" }, // blue
  EDITED: { fillColor: "#28a745", strokeColor: "#1e7e34" }, // green
  ACTIVE: { fillColor: "#ff4f4f", strokeColor: "#FF0000" }, // red
};

export const BASE_POLYGON_OPTIONS: google.maps.PolygonOptions = {
  fillOpacity: 0.5,
  strokeOpacity: 0.8,
  strokeWeight: 2,
};

/**
 * One fence on the map. Only the selected fence is editable and draggable:
 * Google draws a drag handle on every vertex and midpoint of an editable
 * polygon, so making all of them editable puts thousands of handles on the
 * map at country zoom. It also stops accidental drags of the wrong fence.
 *
 * Memoised so that hovering or editing one fence doesn't re-run setOptions on
 * every other polygon instance.
 */
const PolygonContainer = memo(function PolygonContainer({
  fence,
  isSelected,
  onSelect,
  onHover,
  onShapeChange,
}: PolygonContainerProps) {
  // Held in state (not a ref) so the listener effect below re-runs once the
  // Google polygon instance exists.
  const [instance, setInstance] = useState<google.maps.Polygon | null>(null);

  const options = useMemo<google.maps.PolygonOptions>(
    () => ({
      ...BASE_POLYGON_OPTIONS,
      ...FENCE_STYLES[isSelected ? "selected" : fence.status],
      zIndex: isSelected ? 2 : 1,
    }),
    [isSelected, fence.status]
  );

  const handleEdit = useCallback(() => {
    if (!instance) return;

    const nextPath = readPolygonPath(instance);
    if (isSameShape(nextPath, fence.coordinates)) return;

    onShapeChange({
      ...fence,
      coordinates: nextPath,
      status: fence.status === "NEW" ? "NEW" : "EDITED",
    });
  }, [instance, fence, onShapeChange]);

  // Vertex listeners live on the polygon's path MVCArray. The Polygon wrapper
  // calls setPath() whenever the `path` prop changes, which swaps in a fresh
  // MVCArray and silently orphans anything bound to the old one. Binding here,
  // keyed on the coordinates, re-attaches after every change so the second and
  // later vertex edits still register. Only needed while the fence is editable.
  useEffect(() => {
    if (!instance || !isSelected) return;

    const path = instance.getPath();
    const listeners = [
      path.addListener("set_at", handleEdit),
      path.addListener("insert_at", handleEdit),
      path.addListener("remove_at", handleEdit),
    ];

    return () => listeners.forEach((listener) => listener.remove());
  }, [instance, isSelected, fence.coordinates, handleEdit]);

  const handleClick = useCallback(() => onSelect(fence.locationId), [onSelect, fence.locationId]);
  const handleMouseOver = useCallback(() => onHover(fence.locationId), [onHover, fence.locationId]);
  const handleMouseOut = useCallback(() => onHover(null), [onHover]);

  return (
    <Polygon
      editable={isSelected}
      draggable={isSelected}
      path={fence.coordinates}
      options={options}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onDragEnd={handleEdit}
      onLoad={setInstance}
      onUnmount={() => setInstance(null)}
    />
  );
});

export default PolygonContainer;
